import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { immediateToast } from "izitoast-react";
import { StoreDispatch } from "../store";
import {
  getWorkflow,
  internalGetWorkflow,
  internalToggleWorkflowTemplateStatus,
  toggleWorkflowStatus,
  ToggleWorkflowStatusInput,
  Workflow
} from "src/network/graphql/workflowService";
import { getInternalReport } from "src/network/graphql/analyticsService";
import { toggleStart, toggleFinish, upsertWorkflowInList } from "src/page/workflows/workflowsSlice";
import { parseJson } from "src/page/workflows/workflowUtils";

export const SCHEDULED_WORKFLOW_RUN_REPORT_TYPE = "SCHEDULED_WORKFLOW_RUN";

// Per-entity slice of the run report's entity_runs attribute.
export interface SchedulerEntityRunSummary {
  outcome?: string;
  active_rules?: number;
  candidates_found?: number;
  messages?: number;
  unparseable_expiry_skipped?: number;
}

export interface SchedulerRunSummary {
  report_date: string;
  outcome?: string;
  run_sydney_day?: string;
  entity_runs: Record<string, SchedulerEntityRunSummary>;
}

interface SchedulerRunAttributes {
  outcome?: string;
  run_sydney_day?: string;
  entity_runs?: Record<string, SchedulerEntityRunSummary>;
}

interface WorkflowDetailState {
  detail: Workflow | null;
  detailLoading: boolean;
  detailError: unknown;
  conflict: boolean;
  latestRun: SchedulerRunSummary | null;
  latestRunLoading: boolean;
}

const initialState: WorkflowDetailState = {
  detail: null,
  detailLoading: false,
  detailError: null,
  conflict: false,
  latestRun: null,
  latestRunLoading: false
};

const workflowDetailSlice = createSlice({
  name: "workflowDetail",
  initialState,
  reducers: {
    fetchDetailStart: (state) => {
      state.detailLoading = true;
      state.detailError = null;
    },
    fetchDetailSuccess: (state, action: PayloadAction<Workflow | null>) => {
      state.detailLoading = false;
      state.detail = action.payload;
      state.conflict = false;
    },
    fetchDetailFail: (state, action) => {
      state.detailLoading = false;
      state.detail = null;
      state.detailError = action.payload;
    },
    setDetail: (state, action: PayloadAction<Workflow>) => {
      if (state.detail?.workflow_id === action.payload.workflow_id) state.detail = action.payload;
    },
    setConflict: (state, action: PayloadAction<boolean>) => {
      state.conflict = action.payload;
    },
    fetchLatestRunStart: (state) => {
      state.latestRunLoading = true;
    },
    fetchLatestRunSuccess: (state, action: PayloadAction<SchedulerRunSummary | null>) => {
      state.latestRunLoading = false;
      state.latestRun = action.payload;
    },
    fetchLatestRunFail: (state) => {
      state.latestRunLoading = false;
      state.latestRun = null;
    },
    resetDetail: (state) => {
      state.detail = null;
      state.detailLoading = false;
      state.detailError = null;
      state.conflict = false;
      state.latestRun = null;
      state.latestRunLoading = false;
    }
  }
});

export const {
  fetchDetailStart,
  fetchDetailSuccess,
  fetchDetailFail,
  fetchLatestRunStart,
  fetchLatestRunSuccess,
  fetchLatestRunFail,
  setDetail,
  setConflict,
  resetDetail
} = workflowDetailSlice.actions;

// Latest platform scheduler fan-out run (SYSTEM-scope report) — internal
// portal only; the secured search_report never returns SYSTEM reports.
export const fetchLatestSchedulerRunAction = () => {
  return async (dispatch: StoreDispatch) => {
    dispatch(fetchLatestRunStart());
    try {
      const response = await getInternalReport({
        from: 0,
        size: 1,
        filter: { report_filter: { report_types: [SCHEDULED_WORKFLOW_RUN_REPORT_TYPE] } }
      });
      const report = response?.data?.internal_search_report?.reports?.[0];

      if (!report) {
        dispatch(fetchLatestRunSuccess(null));
        return;
      }
      const attributes = parseJson<SchedulerRunAttributes>(report.data);

      dispatch(
        fetchLatestRunSuccess({
          report_date: report.report_date,
          outcome: attributes?.outcome,
          run_sydney_day: attributes?.run_sydney_day,
          entity_runs: attributes?.entity_runs ?? {}
        })
      );
    } catch (error) {
      dispatch(fetchLatestRunFail());
      immediateToast("error", {
        message: (error as Error)?.message || "Failed to load the latest scheduler run",
        timeout: 3000,
        position: "topCenter"
      });
    }
  };
};

export interface FetchWorkflowDetailParams {
  workflow_id: string;
  admin?: boolean;
  organisation_id?: string;
}

export const fetchWorkflowDetailAction = (params: FetchWorkflowDetailParams) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(fetchDetailStart());
    try {
      let workflow: Workflow | undefined;

      if (params.admin && params.organisation_id) {
        const response = await internalGetWorkflow({
          workflow_id: params.workflow_id,
          organisation_id: params.organisation_id
        });

        workflow = response?.data?.internal_get_workflow;
      } else {
        const response = await getWorkflow(params.workflow_id);

        workflow = response?.data?.get_workflow;
      }

      dispatch(fetchDetailSuccess(workflow ?? null));
      return workflow ?? null;
    } catch (error) {
      dispatch(fetchDetailFail(error));
      immediateToast("error", {
        message: (error as Error)?.message || "Failed to load workflow",
        timeout: 3000,
        position: "topCenter"
      });
      return null;
    }
  };
};

export interface ToggleWorkflowStatusFromDetailParams extends ToggleWorkflowStatusInput {
  admin?: boolean;
}

export const toggleWorkflowStatusFromDetailAction = (
  params: ToggleWorkflowStatusFromDetailParams,
  cb?: (_workflow: Workflow) => void,
  fcb?: () => void
) => {
  return async (dispatch: StoreDispatch) => {
    const { admin, ...input } = params;

    dispatch(toggleStart(input.workflow_id));
    try {
      const response = admin
        ? await internalToggleWorkflowTemplateStatus(input)
        : await toggleWorkflowStatus(input);
      const workflow: Workflow | undefined = admin
        ? response?.data?.internal_toggle_workflow_template_status
        : response?.data?.toggle_workflow_status;

      dispatch(toggleFinish(input.workflow_id));
      if (workflow) {
        dispatch(setDetail(workflow));
        dispatch(upsertWorkflowInList(workflow));
        immediateToast("success", {
          message: workflow.active ? "Workflow activated" : "Workflow deactivated",
          timeout: 3000,
          position: "topCenter"
        });
        cb?.(workflow);
      }
      return workflow ?? null;
    } catch (error) {
      dispatch(toggleFinish(input.workflow_id));
      fcb?.();
      return null;
    }
  };
};

export default workflowDetailSlice.reducer;
